import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { formatDate } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';

import { PrestamosService } from '../../../../../modules/finanzas/prestamos/prestamos.service';
import {
  ConstitucionPrestamosModel,
  DeudaACortoPlazoPrestamosModel,
  PagoCuotasPrestamosModel
} from '../../../../../modules/finanzas/prestamos/prestamos.model';
import { ActualSelected, Utils } from '../../../../../shared/utils';
import { ConfirmService } from '../../../../shared/services/confirm.service';
import { NotificationService } from '../../../../shared/services/notification.service';
import { TierService } from '../../../../shared/services/tier.service';
import { ConfirmDialogDetail } from '../../../../shared/components/confirm-dialog.component';
import { LoanOperationDialogComponent } from '../loan-operation-dialog/loan-operation-dialog.component';
import {
  LOAN_DURATION_LONG,
  LOAN_SETTLEMENT_LABELS,
  LoanOperationKind,
  LoanRow,
  loanDurationLabel,
  loanDurationVariant,
  roundMoney,
  stripLoanConcept
} from '../../models/loan.model';

interface LoanMovementLine {
  uid: string;
  date: Date | string;
  concept: string;
  meta: string;
  amount: number;
  interest: number;
}

interface LoanMovementBlock {
  kind: LoanOperationKind;
  title: string;
  icon: string;
  singular: string;
  amountLabel: string;
  deleteMessage: string;
  total: number;
  count: number;
  lines: LoanMovementLine[];
}

const MAX_LINES = 5;

@Component({
  selector: 'app-loan-detail',
  templateUrl: './loan-detail.component.html',
  styleUrls: ['./loan-detail.component.scss'],
  standalone: false,
})
export class LoanDetailComponent implements OnChanges {
  @Input() row!: LoanRow;
  @Output() changed = new EventEmitter<void>();
  @Output() edit = new EventEmitter<void>();

  readonly maxLines = MAX_LINES;
  readonly year = ActualSelected.yearSelected;

  loading = true;
  error = false;
  blocks: LoanMovementBlock[] = [];

  paidCapital = 0;
  paidInterest = 0;
  paidBankFees = 0;

  private loadedSignature = '';

  constructor(
    private prestamosService: PrestamosService,
    private dialog: MatDialog,
    private confirm: ConfirmService,
    private notify: NotificationService,
    private tier: TierService,
  ) { }

  get isLongTerm(): boolean {
    return !!this.row && this.row.duracion === LOAN_DURATION_LONG;
  }

  get durationLabel(): string {
    return loanDurationLabel(this.row && this.row.duracion);
  }

  get durationVariant() {
    return loanDurationVariant(this.row && this.row.duracion);
  }

  get settlementLabel(): string {
    return (this.row && LOAN_SETTLEMENT_LABELS[this.row.tipoLiquidaciones]) || '—';
  }

  hasValue(value: any): boolean {
    return value !== null && value !== undefined && value !== '';
  }

  ngOnChanges(): void {
    if (!this.row || !this.row.uid) return;
    const signature = [this.row.uid, this.row.importeInicial, this.row.importePendiente, this.row.duracion].join('|');
    if (signature === this.loadedSignature) return;
    this.loadedSignature = signature;
    this.load(this.row.uid);
  }

  retry(): void {
    if (!this.row || !this.row.uid) return;
    this.load(this.row.uid);
  }

  add(block: LoanMovementBlock): void {
    this.openDialog(block);
  }

  manage(block: LoanMovementBlock): void {
    this.openDialog(block);
  }

  editLine(block: LoanMovementBlock, line: LoanMovementLine): void {
    this.openDialog(block, line.uid);
  }

  deleteLine(block: LoanMovementBlock, line: LoanMovementLine): void {
    if (!this.tier.requireAdvancedTier()) return;
    this.confirm.open({
      title: `Eliminar ${block.singular}`,
      subtitle: [this.row.entidad, this.row.numero ? 'Nº ' + this.row.numero : null].filter(Boolean).join(' · ') || undefined,
      message: block.deleteMessage,
      tone: 'danger',
      details: this.details(block, line),
      confirmText: 'Eliminar',
      confirmIcon: 'delete',
      loadingText: 'Eliminando…',
      errorText: 'No se pudo eliminar el movimiento',
      action: () => this.deleteRequest(block.kind, line.uid).then(ok => { if (ok !== true) throw new Error(); }),
    }).subscribe(deleted => {
      if (!deleted) return;
      this.notify.ok('Movimiento eliminado correctamente');
      this.changed.emit();
    });
  }

  trackByUid(_: number, line: LoanMovementLine): string {
    return line.uid;
  }

  trackByKind(_: number, block: LoanMovementBlock): string {
    return block.kind;
  }

  private openDialog(block: LoanMovementBlock, editUid?: string): void {
    if (!this.tier.requireAdvancedTier()) return;
    LoanOperationDialogComponent.open(this.dialog, { kind: block.kind, loan: this.row, editUid })
      .subscribe(changed => { if (changed) this.changed.emit(); });
  }

  private deleteRequest(kind: LoanOperationKind, uid: string): Promise<boolean> {
    if (kind === 'constitution') return this.prestamosService.constitucionDelete(uid);
    if (kind === 'shortTermDebt') return this.prestamosService.deudaACortoPlazoDelete(uid);
    return this.prestamosService.pagoCoutasDelete(uid);
  }

  private load(uid: string): void {
    this.loading = !this.blocks.length;
    this.error = false;

    Promise.all([
      this.prestamosService.pagoCoutasList(uid),
      this.prestamosService.constitucionList(uid),
      this.isLongTerm ? this.prestamosService.deudaACortoPlazoList(uid) : Promise.resolve([] as DeudaACortoPlazoPrestamosModel[]),
    ])
      .then(([installments, constitutions, shortTermDebts]) => {
        if (!this.row || uid !== this.row.uid) return;
        const installmentList = Array.isArray(installments) ? installments : [];
        this.paidCapital = this.sum(installmentList, i => i.importeCapital);
        this.paidInterest = this.sum(installmentList, i => i.intereses);
        this.paidBankFees = this.sum(installmentList, i => i.gastosBancarios);

        const blocks = [
          this.buildBlock('installment', 'Cuotas pagadas', 'payment', 'pago de cuota', 'Importe capital',
            'Se eliminará el pago y su asiento contable, y el capital volverá a sumarse al importe pendiente del préstamo. Si estaba conciliado, el movimiento bancario volverá a quedar pendiente.',
            this.installmentLines(installmentList)),
          this.buildBlock('constitution', 'Constitución', 'account_balance', 'constitución', 'Importe',
            'Se eliminará la constitución y su asiento contable. Si estaba conciliada, el movimiento bancario volverá a quedar pendiente.',
            this.constitutionLines(Array.isArray(constitutions) ? constitutions : [])),
        ];
        if (this.isLongTerm) {
          blocks.push(this.buildBlock('shortTermDebt', 'Deuda reclasificada a corto plazo', 'swap_horiz', 'reclasificación', 'Importe',
            'Se eliminará la reclasificación a corto plazo y su asiento contable.',
            this.shortTermDebtLines(Array.isArray(shortTermDebts) ? shortTermDebts : [])));
        }
        this.blocks = blocks;
      })
      .catch(() => { this.error = true; })
      .then(() => { this.loading = false; });
  }

  private installmentLines(list: PagoCuotasPrestamosModel[]): LoanMovementLine[] {
    return list.map(item => ({
      uid: item.uid,
      date: item.fecha,
      concept: stripLoanConcept(item.concepto, this.row) || 'Sin concepto',
      meta: [
        item.intereses ? 'Intereses ' + Utils.FormatMoney(item.intereses) + ' €' : '',
        item.gastosBancarios ? 'Gastos ' + Utils.FormatMoney(item.gastosBancarios) + ' €' : '',
        item.bankAccountInfo || '',
      ].filter(Boolean).join(' · '),
      amount: Number(item.importeCapital) || 0,
      interest: Number(item.intereses) || 0,
    }));
  }

  private constitutionLines(list: ConstitucionPrestamosModel[]): LoanMovementLine[] {
    return list.map(item => ({
      uid: item.uid,
      date: item.fecha,
      concept: stripLoanConcept(item.concepto, this.row) || 'Sin concepto',
      meta: [
        item.comisionApertura ? 'Comisión ' + Utils.FormatMoney(item.comisionApertura) + ' €' : '',
        item.gastosBancarios ? 'Gastos ' + Utils.FormatMoney(item.gastosBancarios) + ' €' : '',
        item.bankAccountInfo || '',
      ].filter(Boolean).join(' · '),
      amount: Number(item.importe) || 0,
      interest: 0,
    }));
  }

  private shortTermDebtLines(list: DeudaACortoPlazoPrestamosModel[]): LoanMovementLine[] {
    return list.map(item => ({
      uid: item.uid,
      date: item.fecha,
      concept: item.concepto || 'Sin concepto',
      meta: item.activitiesName || '',
      amount: Number(item.importe) || 0,
      interest: 0,
    }));
  }

  private buildBlock(kind: LoanOperationKind, title: string, icon: string, singular: string, amountLabel: string, deleteMessage: string, lines: LoanMovementLine[]): LoanMovementBlock {
    const total = roundMoney(lines.reduce((acc, line) => acc + line.amount, 0));
    return { kind, title, icon, singular, amountLabel, deleteMessage, total, count: lines.length, lines: lines.slice(0, MAX_LINES) };
  }

  private details(block: LoanMovementBlock, line: LoanMovementLine): ConfirmDialogDetail[] {
    const details: ConfirmDialogDetail[] = [];
    if (line.date) details.push({ label: 'Fecha', value: formatDate(line.date, 'dd/MM/yyyy', 'es-ES') });
    details.push({ label: 'Concepto', value: line.concept });
    details.push({ label: block.amountLabel, value: Utils.FormatMoney(line.amount) + ' €' });
    if (block.kind === 'installment') details.push({ label: 'Intereses', value: Utils.FormatMoney(line.interest) + ' €' });
    return details;
  }

  private sum<T>(items: T[], pick: (item: T) => number): number {
    return roundMoney(items.reduce((acc, item) => acc + (Number(pick(item)) || 0), 0));
  }
}
